import { Flex, Spacer } from "@hackthenorth/north";
import { Button, DropdownMenu, DropdownMenuSection } from "north.js";
import React, { useMemo } from "react";
import styled from "styled-components";

import { CheckIcon, FilterIcon } from "src/static/icons";
import { SUPPLEMENTARY_FILTER_COLOURS } from "src/views/schedule/constants";

import { TSupplementaryRoles } from "../../shared/constants/roles";
import { FILTERNAMES } from "../constants";

interface ScheduleFilterBarProps {
  shiftFilters: string[];
  setShiftFilters: (filters: string[]) => void;
  /** lets a page reset pagination when the filter set changes */
  onFiltersChange?: () => void;
}

/**
 * The category chip row and its supplementary sub-filter dropdown, shared by the
 * volunteer and admin schedules so the two cannot drift apart.
 */
export const ScheduleFilterBar: React.FC<ScheduleFilterBarProps> = ({
  shiftFilters,
  setShiftFilters,
  onFiltersChange,
}) => {
  const applyFilters = (next: string[]) => {
    setShiftFilters(next);
    onFiltersChange?.();
  };

  // toggling Supplementary carries all of its sub-roles with it
  const toggleRole = (role: string) => {
    const wasActive = shiftFilters.includes(role);
    let next = wasActive
      ? shiftFilters.filter((shift) => shift !== role)
      : [...shiftFilters, role];

    if (role === "Supplementary") {
      const subRoles = Object.values(TSupplementaryRoles) as string[];
      next = wasActive
        ? next.filter((shift) => !subRoles.includes(shift))
        : [...next, ...subRoles.filter((sub) => !next.includes(sub))];
    }

    applyFilters(next);
  };

  const filterItems = useMemo((): DropdownMenuSection[] => {
    const toggleSubRole = (role: string) =>
      applyFilters(
        shiftFilters.includes(role)
          ? shiftFilters.filter((shift) => shift !== role)
          : [...shiftFilters, role]
      );

    return [
      Object.values(TSupplementaryRoles).map((item) => ({
        content: (
          <FilterRow>
            <Dot
              color={SUPPLEMENTARY_FILTER_COLOURS[item as TSupplementaryRoles]}
            />
            <DropdownText>{item}</DropdownText>
            {shiftFilters.includes(item) && <StyledCheckIcon />}
          </FilterRow>
        ),
        selected: shiftFilters.includes(item),
        dismissOnClick: false,
        action: () => toggleSubRole(item),
      })),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shiftFilters]);

  return (
    <Flex row style={{ justifyContent: "space-between", alignItems: "center" }}>
      <ChipRow>
        {FILTERNAMES.map((role) => (
          <FilterButton
            key={role}
            type="button"
            active={shiftFilters.includes(role)}
            aria-pressed={shiftFilters.includes(role)}
            onClick={() => toggleRole(role)}
          >
            {role}
          </FilterButton>
        ))}
      </ChipRow>
      {shiftFilters.includes("Supplementary") && (
        <StyledDropdown items={filterItems}>
          <Button>
            <Flex style={{ alignItems: "center" }}>
              <FilterIcon />
              <Spacer width={8} />
              <ButtonText>Filter</ButtonText>
            </Flex>
          </Button>
        </StyledDropdown>
      )}
    </Flex>
  );
};

/** Bottom padding is the gap above the calendar/list content under the chips. */
const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  padding: 16px 0 24px;
`;

const FilterButton = styled.button<{ active: boolean }>`
  border: none;
  border-radius: 27.568px;
  background: ${({ theme, active }) =>
    active
      ? theme.globalConstants.color.brandPrimary
      : theme.globalConstants.color.backgroundGrayDark};
  color: ${({ theme, active }) =>
    active
      ? theme.globalConstants.color.white
      : theme.globalConstants.color.textSecondary};
  font-size: 16px;
  font-weight: 700;
  padding: 4px 9.189px;
  cursor: pointer;
`;

const ButtonText = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-weight: 700;
  font-size: 16px;
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
`;

const StyledDropdown = styled(DropdownMenu)`
  z-index: 1;
`;

const Dot = styled.div<{ color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ color }) => color};
`;

const DropdownText = styled.p`
  margin-top: 0;
  margin-left: 8px;
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-weight: 400;
  font-size: 16px;
`;

const StyledCheckIcon = styled(CheckIcon)`
  width: 16px;
  margin-left: 8px;
  color: ${({ theme }) => theme.globalConstants.color.textPrimary};
`;

export default ScheduleFilterBar;
